import React, { Suspense } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, RouteComponentProps, useLocation } from "react-router-dom";

import { WORKSPACE_BASE_ROUTE, generateListenerSessionExistPage } from "./constants";
import { RouterProps } from "./types";
import { checkIsListener, isAuthenticated } from "../store/account";
import { LoaderWithDescription } from "../components/core/Loader/LoaderWithDescription";
import useUserAuth from "../utils/hooks/useUserAuth";
import { useQuery } from "../utils/hooks/useQuery";
import { log, LogCategory } from "../utils/log";

const logE = (event: string, ...args: any[]) => log(LogCategory.Router, "##### " + event, ...args);

export interface PublicRouterProps extends Omit<RouterProps, "component"> {
  component: React.LazyExoticComponent<any>;
  redirect?: boolean;
  step?: boolean;
}

export function PublicRoute({ component: Component, redirect = true, step = false, ...rest }: PublicRouterProps) {
  const authenticated = useSelector(isAuthenticated);
  const isListener = useSelector(checkIsListener);
  const userLoaded = useUserAuth();

  const location = useLocation();
  const path = location.pathname;

  const isListenerURL = path.startsWith("/listener/join");
  const listenerId = path.split("/")[3];
  const query = useQuery();
  const listenerMode = query.get("mode");

  logE("PublicRoute", path);

  if (!userLoaded) {
    logE("PrivateRoute: not userLoaded");
    return <LoaderWithDescription />;
  }

  if (authenticated && isListenerURL && !isListener) {
    logE("PrivateRoute: Listener session exist");
    return <Redirect to={generateListenerSessionExistPage(listenerId, listenerMode)} />;
  }

  if (authenticated && redirect && !isListener) {
    logE("PrivateRoute: navigate to workspace");
    return <Redirect to={{ pathname: WORKSPACE_BASE_ROUTE, state: { from: location } }} />;
  }

  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps) => (
        <Suspense fallback={<LoaderWithDescription />}>
          <Component {...props} step={step} />
        </Suspense>
      )}
    />
  );
}
